/**
 * What deleting an account deletes, what it keeps and for how long. One
 * source for the deletion page (which the server also writes into the page's
 * HTML, for readers without JavaScript), the delete dialog, the Privacy page
 * and the tests, so that none of them says something the others do not.
 */
import { REPORT_KEPT_DAYS } from './moderation.js';

/** How long a link to delete an account works, once it has been emailed. The emails and pages say "one hour". */
export const DELETION_LINK_TTL_MINUTES = 60;
/** How long the email confirming that an account was deleted is kept, for questions about when that happened. */
export const DELETION_CONFIRMATION_KEPT_DAYS = 30;
/** How long the record of a device is kept after it was signed out, or its sign-in ran out. */
export const SIGNED_OUT_DEVICE_KEPT_DAYS = 30;
/**
 * How long the token Apple gave for an account that signed in with Apple
 * outlives the account, at most: until Apple confirms Meet Dost is gone from
 * the person's Apple Account (server/src/auth/apple-revocations.ts).
 */
export const APPLE_TOKEN_KEPT_DAYS = 3;
/** How long what a connected product was sent (its webhooks) is kept, to look into a delivery that went wrong. */
export const WEBHOOK_RECORD_KEPT_DAYS = 30;
/** What other people's meeting histories call someone who took part and has since deleted their account. */
export const DELETED_ACCOUNT_NAME = 'Deleted account';

export interface DeletionFactsInput {
  appName: string;
  /**
   * For the iPhone, iPad and Android apps. They say nothing about paying: the
   * App Store and Google Play do not allow pointing to payment elsewhere.
   */
  inApp: boolean;
  /** This service sells plans. */
  plansSold: boolean;
  /** The ways to sign in that this server offers, by name: ['Google', 'Microsoft']. */
  providers: readonly string[];
  /** How long database backups are kept (BACKUP_RETENTION_DAYS). */
  backupDays: number | null;
}

export interface DeletionFacts {
  deleted: string[];
  kept: string[];
  /** What has to be true before an account can be deleted. Empty in the apps. */
  conditions: string[];
  /** How to delete only some of your data and keep the account. */
  partial: string[];
  /** "The Google account you sign in with is not affected." null when this server offers no way to sign in. */
  notAffected: string | null;
}

const days = (n: number): string => (n === 1 ? '1 day' : `${n} days`);

export function deletionFacts(input: DeletionFactsInput): DeletionFacts {
  const web = !input.inApp;
  const deleted = [
    'Your profile: your name, your email address and the ways you sign in.',
    'Every device you are signed in on, which is signed out.',
    'Your meeting links, and the history of your meetings: when each one ran, who joined, and its chat.',
    'The recordings you made, with their video files.',
    'Who you shared those recordings with, and our copies of the emails that told them.',
    'The messages you sent in other people’s meetings.',
    `Your name in the list of people who joined other people’s meetings, which then says “${DELETED_ACCOUNT_NAME}”.`,
    ...(input.plansSold ? [web ? 'A plan you pay for, which is cancelled at once, so you are not charged again.' : 'Your plan, which ends.'] : []),
    'The emails we sent you, from our records.',
  ];
  const kept = [
    'Recordings other people made of your meetings. They belong to whoever recorded them, and stay until that person deletes them.',
    'Your email address on the recordings other people shared with you, until they take it off or delete the recording.',
    `When a meeting was arranged through another product (a CRM, for example), that product was sent its chat and recordings, and keeps them by its own rules. Our record of what was sent goes after ${days(WEBHOOK_RECORD_KEPT_DAYS)}.`,
    `Reports you sent, or that were about you, with the name you used and a copy of the messages concerned, but not your account: until ${days(REPORT_KEPT_DAYS)} after each was closed.`,
    `The email confirming that your account was deleted, for ${days(DELETION_CONFIRMATION_KEPT_DAYS)}.`,
    ...(input.providers.includes('Apple')
      ? [`If you signed in with Apple: the key Apple gave us for your Apple Account, only until Apple confirms that ${input.appName} is removed from it, at most ${days(APPLE_TOKEN_KEPT_DAYS)}.`]
      : []),
    ...(input.backupDays ? [`Copies in our database backups, until those backups are replaced: at most ${days(input.backupDays)}.`] : []),
    ...(web && input.plansSold ? ['Your payment history and invoices, which stay with Stripe, the company that handles payments, for accounting and as tax law requires.'] : []),
  ];
  const conditions = web
    ? [
        ...(input.plansSold ? ['A plan you pay for is cancelled as part of deleting the account. If that cannot be done at that moment, nothing is deleted and you are told why.'] : []),
        `An account that looks after a product connected to ${input.appName} (only the people who run the service have one) can be deleted once another of them takes it over, or it is revoked. You are told if this applies.`,
      ]
    : [];
  const partial = [
    'A recording: on the Recordings page, choose Delete next to it.',
    'The chat of a meeting you hosted: on the home screen, under Recent, open the meeting’s chat and choose Delete chat.',
    'A signed-in device: in Settings, under Devices, choose Sign out next to it, or sign out of all other devices at once.',
  ];
  // "Google", "Google or Apple", "Google, Microsoft or Apple".
  const names = input.providers.length > 1 ? `${input.providers.slice(0, -1).join(', ')} or ${input.providers.at(-1)}` : input.providers[0];
  const notAffected = names ? `The ${names} account you sign in with is not affected.` : null;
  return { deleted, kept, conditions, partial, notAffected };
}

/**
 * How a deletion is confirmed: by typing the account's address, or, for an
 * address Apple made up (Hide My Email), which nobody can type from memory,
 * the word delete. `apple`: this server offers Sign in with Apple.
 */
export function deletionConfirmText(apple: boolean): string {
  return `type your email address to confirm${apple ? ', or the word delete if Apple hid your address' : ''}`;
}

/** The ways to delete an account, one sentence each, for the deletion page and the Privacy page. */
export function deletionWays(appName: string, apple: boolean): { app: string; web: string } {
  const confirm = deletionConfirmText(apple);
  return {
    app: `In the ${appName} app: open Settings, choose Delete account at the bottom, and ${confirm}.`,
    web: `On the website: sign in, open Settings, choose Delete account at the bottom, and ${confirm}.`,
  };
}
